/**
 * Cronômetro persistente da barra de reserva do cart drawer.
 * O prazo vive em sessionStorage e, portanto, continua após atualizações AJAX.
 */
class CartReservationTimer extends HTMLElement {
  /** Inicia a contagem quando o componente entra no documento. */
  connectedCallback() {
    this.timeElement = this.querySelector('[data-cart-timer-value]');
    this.durationMilliseconds = this.getDurationMilliseconds();
    this.storageKey = this.dataset.storageKey;
    this.shouldRestart = this.dataset.restart === 'true';

    // Sem o alvo ou duração válida, o componente permanece estático e seguro.
    if (!this.timeElement || this.durationMilliseconds <= 0) return;

    this.deadline = this.getDeadline();
    this.updateTimer();
    this.interval = window.setInterval(() => this.updateTimer(), 1000);
  }

  /** Interrompe o intervalo quando o drawer é substituído por uma resposta AJAX. */
  disconnectedCallback() {
    // O intervalo só existe depois de uma inicialização válida.
    if (this.interval) window.clearInterval(this.interval);
  }

  /** Converte a duração configurada no editor de minutos para milissegundos. */
  getDurationMilliseconds() {
    const minutes = Number.parseInt(this.dataset.durationMinutes, 10);

    // Valores inválidos são transformados em zero para impedir NaN no contador.
    if (!Number.isFinite(minutes) || minutes <= 0) return 0;

    return minutes * 60 * 1000;
  }

  /** Recupera um prazo válido da sessão ou cria um novo prazo. */
  getDeadline() {
    const storedDeadline = this.getStoredDeadline();
    const now = Date.now();

    // Um prazo futuro preserva a contagem entre recargas e mudanças de quantidade.
    if (Number.isFinite(storedDeadline) && storedDeadline > now) return storedDeadline;

    // Sem reinício, um prazo expirado permanece encerrado durante a sessão.
    if (Number.isFinite(storedDeadline) && storedDeadline <= now && !this.shouldRestart) return 0;

    const newDeadline = now + this.durationMilliseconds;
    this.storeDeadline(newDeadline);
    return newDeadline;
  }

  /** Lê o prazo sem quebrar navegadores que bloqueiam o armazenamento da sessão. */
  getStoredDeadline() {
    try {
      return Number.parseInt(window.sessionStorage.getItem(this.storageKey), 10);
    } catch (error) {
      // Na ausência de storage, NaN força um prazo apenas para a instância atual.
      return Number.NaN;
    }
  }

  /** Persiste o prazo quando sessionStorage está disponível. */
  storeDeadline(deadline) {
    try {
      window.sessionStorage.setItem(this.storageKey, String(deadline));
    } catch (error) {
      // O cronômetro continua funcional em memória quando o storage é bloqueado.
    }
  }

  /** Atualiza o texto do cronômetro e trata o término da reserva. */
  updateTimer() {
    const now = Date.now();
    let remainingMilliseconds = Math.max(0, this.deadline - now);

    // Quando habilitado, o reinício mantém uma nova janela de reserva após zero.
    if (remainingMilliseconds === 0 && this.shouldRestart) {
      this.deadline = now + this.durationMilliseconds;
      this.storeDeadline(this.deadline);
      remainingMilliseconds = this.durationMilliseconds;
    }

    const remainingSeconds = Math.ceil(remainingMilliseconds / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    this.timeElement.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    // Sem reinício, não há motivo para manter o intervalo ativo depois de 00:00.
    if (remainingMilliseconds === 0 && !this.shouldRestart && this.interval) {
      this.storeDeadline(0);
      window.clearInterval(this.interval);
      this.interval = null;
    }
  }
}

// A proteção evita erro caso o asset seja carregado novamente pelo editor.
if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}

